import React, { useState, useEffect } from "react"
import { useNavigate, Link, useSearchParams } from "react-router-dom"
import { useUpdateQuarterlyReport } from "~/api/quarterly_reports"
import {
  reportsPath,
  reportSectionCoverLetterPath,
  reportSectionTableOfContentsPath,
  reportSectionLpHighLevelKpisPath,
  reportSectionLpInvestmentPerformancePath,
  reportSectionAttributionExposureLetterPath,
  reportSectionExposureBySectorPath,
  reportSectionExposureByGeographyPath,
  reportSectionQpcspIntroLetterPath,
  reportSectionLpCapitalStatementPath,
  reportSectionLogosCompanyCountsPath,
  reportSectionHistoricalInvestmentPerformancePath,
  reportSectionQpcspFinancialStatementPath,
} from "@/QuarterlyReportApp"
import {
  VALID_SECTIONS,
  ALWAYS_ENABLED_SECTIONS,
  ReportSection,
} from "~/api/communications"
import { Button } from "@/ui/Button"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { Eye } from "lucide-react"
import DebugModal from "./DebugModal"

const QuarterlyReportShow: React.FC = () => {
  const { communicationId } = useCommunicationContext()
  const { reportId, report, isLoading, error } = useQuarterlyReportContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [showDebugModal, setShowDebugModal] = useState(false)
  const updateReportMutation = useUpdateQuarterlyReport()

  const isDebugMode = searchParams.get("debug") === "true"

  useEffect(() => {
    if (report && report.sections) {
      setSelectedSections(report.sections)
    }
  }, [report])

  const handleSectionToggle = (section: string) => {
    const newSelectedSections = selectedSections.includes(section)
      ? selectedSections.filter((s) => s !== section)
      : [...selectedSections, section]

    setSelectedSections(newSelectedSections)

    if (reportId) {
      updateReportMutation.mutate({
        id: reportId,
        sections: newSelectedSections,
      })
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSections = e.target.checked ? [...VALID_SECTIONS] : []
    setSelectedSections(newSections)

    if (reportId) {
      updateReportMutation.mutate({
        id: reportId,
        sections: newSections,
      })
    }
  }

  const areAllSelected =
    VALID_SECTIONS.length > 0 &&
    VALID_SECTIONS.every((section) => selectedSections.includes(section))

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading report details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <>
      <QuarterlyReportLayout
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span>{report.household_name}</span>
            {isDebugMode && report.debug && (
              <button
                onClick={() => setShowDebugModal(true)}
                style={{
                  fontSize: "14px",
                  padding: "4px 12px",
                  background: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                🔍 Debug Data
              </button>
            )}
          </div>
        }
        subtitle="Select the sections you want to include in the report:"
        actions={
          <Button
            size="md"
            variant="outline"
            onClick={() => navigate(reportsPath({ communicationId }))}
          >
            Close
          </Button>
        }
        breadcrumbProps={{
          householdName: report.household_name,
        }}
      >
        <div className="table-container">
          <table className="sections-table">
            <tbody>
              {ALWAYS_ENABLED_SECTIONS.map((section) => (
                <tr key={section} className="selected">
                  <td className="checkbox-column">
                    <input type="checkbox" checked disabled />
                  </td>
                  <td>
                    <span>{section}</span>
                    {section === ReportSection.COVER_LETTER && (
                      <Link
                        to={reportSectionCoverLetterPath({
                          communicationId,
                          reportId,
                        })}
                        className="edit-link"
                      >
                        {" "}
                        View
                      </Link>
                    )}
                    {section === ReportSection.TABLE_OF_CONTENTS && (
                      <Link
                        to={reportSectionTableOfContentsPath({
                          communicationId,
                          reportId,
                        })}
                        className="edit-link"
                      >
                        {" "}
                        View
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
              {VALID_SECTIONS.map((section) => {
                return (
                  <tr
                    key={section}
                    className={
                      selectedSections.includes(section) ? "selected" : ""
                    }
                  >
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedSections.includes(section)}
                        onChange={() => handleSectionToggle(section)}
                      />
                    </td>
                    <td>
                      <span>{section}</span>
                      {section === ReportSection.LP_HIGH_LEVEL_KPIS && (
                        <Link
                          to={reportSectionLpHighLevelKpisPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.LP_INVESTMENT_PERFORMANCE && (
                        <Link
                          to={reportSectionLpInvestmentPerformancePath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section ===
                        ReportSection.ATTRIBUTION_EXPOSURE_LETTER && (
                        <Link
                          to={reportSectionAttributionExposureLetterPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.EXPOSURE_BY_SECTOR && (
                        <Link
                          to={reportSectionExposureBySectorPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.EXPOSURE_BY_GEOGRAPHY && (
                        <Link
                          to={reportSectionExposureByGeographyPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          View
                        </Link>
                      )}
                      {section === ReportSection.QPCSP_INTRO_LETTER && (
                        <Link
                          to={reportSectionQpcspIntroLetterPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.LP_CAPITAL_STATEMENT && (
                        <Link
                          to={reportSectionLpCapitalStatementPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.LOGOS_COMPANY_COUNTS && (
                        <Link
                          to={reportSectionLogosCompanyCountsPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section ===
                        ReportSection.HISTORICAL_INVESTMENT_PERFORMANCE && (
                        <Link
                          to={reportSectionHistoricalInvestmentPerformancePath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                      {section === ReportSection.QPCSP_FINANCIAL_STATEMENT && (
                        <Link
                          to={reportSectionQpcspFinancialStatementPath({
                            communicationId,
                            reportId,
                          })}
                          className="edit-link"
                        >
                          {" "}
                          Edit
                        </Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </QuarterlyReportLayout>

      {showDebugModal && report.debug && (
        <DebugModal
          debugData={report.debug}
          onClose={() => setShowDebugModal(false)}
        />
      )}
    </>
  )
}

export default QuarterlyReportShow
